/**
 * Housing Calculator - landlord break-even engine
 *
 * The question: what rent does a rental property have to clear for buying it to leave you no worse
 * off than investing the same cash instead?
 *
 * Both sides run through the same fund model the rent-vs-buy side uses, so the rent this solves for
 * is genuinely the rent at which the two finish level. Keeping the search and the numbers the UI
 * displays on one code path is the whole point: when they were separate, they disagreed.
 */

import { MonthlyCashFlow, PurchaseSummary } from '../../types/housingComparison';
import { simulateInvestmentFund, InvestmentFundMonth } from './rentVsBuyEngine';

export interface LandlordParams {
  monthlyRent: number;
  rentalInflationRate: number;
  propertyManagementFeePercent: number;
  vacancyRatePercent: number;
  /** Net rental income is ordinary income, taxable at the full marginal rate. */
  marginalTaxRate: number;
  investmentReturnRate: number;
  investmentTaxRate: number;
}

export interface LandlordCashFlow extends MonthlyCashFlow {
  rentalIncome: number;
  propertyManagementFee: number;
  vacancyLoss: number;
  netRentalIncome: number;
  rentalIncomeTax: number;
  landlordNetCashFlow: number;
}

/**
 * The property's own cash position each month at a given rent.
 *
 * Deductible against rental income: interest, property tax, maintenance, both insurances and condo
 * fees. Principal is not deductible — it buys equity rather than paying for anything. Management
 * fees and vacancy are already out of net rental income, so deducting them again would double up.
 * A rental loss produces a negative tax, which is a refund and so adds to cash flow.
 */
export const buildLandlordCashFlows = (
  ownerCashFlows: MonthlyCashFlow[],
  params: LandlordParams
): LandlordCashFlow[] => {
  const managementFeeRate = params.propertyManagementFeePercent / 100;
  const vacancyRate = params.vacancyRatePercent / 100;
  const taxRate = params.marginalTaxRate / 100;
  const annualRentInflation = params.rentalInflationRate / 100;

  return ownerCashFlows.map(cashFlow => {
    const rentalIncome = params.monthlyRent * Math.pow(1 + annualRentInflation, cashFlow.year - 1);
    const propertyManagementFee = rentalIncome * managementFeeRate;
    const vacancyLoss = rentalIncome * vacancyRate;
    const netRentalIncome = rentalIncome - propertyManagementFee - vacancyLoss;

    const deductibleExpenses =
      cashFlow.interestPaid +
      cashFlow.propertyTax +
      cashFlow.maintenance +
      cashFlow.homeInsurance +
      cashFlow.mortgageInsurance +
      cashFlow.condoFees;

    const rentalIncomeTax = (netRentalIncome - deductibleExpenses) * taxRate;
    const landlordNetCashFlow = netRentalIncome - cashFlow.totalMonthlyExpense - rentalIncomeTax;

    return {
      ...cashFlow,
      rentalIncome,
      propertyManagementFee,
      vacancyLoss,
      netRentalIncome,
      rentalIncomeTax,
      landlordNetCashFlow
    };
  });
};

export interface LandlordSimulation {
  cashFlows: LandlordCashFlow[];
  fund: InvestmentFundMonth[];
  /** The fund after cashing out and paying tax on the gain, for comparison with net sale proceeds. */
  finalFundBalance: number;
}

/**
 * Buying the property, against investing the same cash instead. The fund is seeded with everything
 * the purchase would tie up and then absorbs the property's monthly cash flow with the sign
 * flipped: a month the property pays for itself is a month the investor can take cash out.
 */
export const simulateLandlord = (
  purchaseSummary: PurchaseSummary,
  params: LandlordParams
): LandlordSimulation => {
  const cashFlows = buildLandlordCashFlows(purchaseSummary.monthlyCashFlows, params);
  const fund = simulateInvestmentFund({
    initialFunding: purchaseSummary.downpayment + purchaseSummary.closingCosts,
    netContributions: cashFlows.map(flow => -flow.landlordNetCashFlow),
    investmentReturnRate: params.investmentReturnRate,
    investmentTaxRate: params.investmentTaxRate
  });

  return {
    cashFlows,
    fund,
    finalFundBalance: fund[fund.length - 1]?.closingBalanceAfterTax ?? 0
  };
};

export interface LandlordBreakEven {
  rent: number;
  /** How far apart the two sides finished. Should be pennies. */
  gap: number;
}

/**
 * The rent at which owning the property and investing the cash instead finish level.
 *
 * The fund falls as rent rises — more rent means more cash the investor does not have to put in —
 * so the search bisects rather than stepping. Bisection converges to the cent every time, which is
 * what lets the comparison panel show two equal numbers and call it a break-even.
 */
export const findLandlordBreakEvenRent = (
  purchaseSummary: PurchaseSummary,
  params: Omit<LandlordParams, 'monthlyRent'>
): LandlordBreakEven => {
  const target = purchaseSummary.netProceedsFromSale;
  const fundAt = (rent: number) => simulateLandlord(purchaseSummary, { ...params, monthlyRent: rent }).finalFundBalance;

  // Bracket the answer first: the fund shrinks as rent rises, so push the ceiling up until it does.
  let low = 0;
  let high = 10000;
  let guard = 0;
  while (fundAt(high) > target && guard < 20) {
    high *= 2;
    guard++;
  }

  if (fundAt(low) < target) {
    // Even free rent leaves the investor behind; no rent can close the gap.
    return { rent: 0, gap: fundAt(0) - target };
  }

  for (let iteration = 0; iteration < 60; iteration++) {
    const middle = (low + high) / 2;
    if (fundAt(middle) > target) {
      low = middle;
    } else {
      high = middle;
    }
  }

  const rent = (low + high) / 2;
  return { rent, gap: fundAt(rent) - target };
};
